/* dl-lightbox.js — design—light PDP · FULL-SCREEN photo viewer
   Click any product photo -> a white full-screen viewer: the photo as large as
   the screen allows, a strip of small square thumbnails along the bottom and a
   ✕ top-right (the "3 / 8" counter is announced to screen readers only).
   Thumbnails / arrows / arrow keys / swipe to move, click-drag-pinch-wheel-
   double-tap to zoom, Esc / ✕ / a click on the empty white to close.

   BINDS TO (product template, inside <div class="dl-scope">):
     .dl-scope .galwrap .gal .sh img     every gallery cell image (incl. .sh.over)
     .dl-scope .galwrap [data-dllb-set]  "Fotky všech variant": every .sh.vph photo
   Adds NOTHING to the page until the first click: the overlay is built lazily and
   appended to <body> (never inside .dl-scope, never inside .galwrap).

   ROLLBACK: git revert of the 2026-10-04 commit restores the in-column viewer;
   removing the two tags from snippets/dl-assets.liquid removes the viewer.

   ── 2026-10-04 REDESIGN: in-column -> full screen. READ BEFORE EDITING ─────────
   The 2026-08-17 in-column viewer (dark scrim over the gallery column only, buy
   box lit beside it, no thumbnails, clicks outside swallowed) read as messy and
   confusing to customers. Owner, 2026-10-04: "a pretty way of showing them in a
   gallery, small icons of the images on the bottom, maximum usability of the rest
   of the screen, a noticeable black/white X top right which, if covering the
   image, shifts its exact pixels into the contrasting colour to stay visible".
   What that reverses, deliberately:
     A. FOOTPRINT is the whole viewport (CSS inset:0). No more --dllb-l/--dllb-w,
        no ResizeObserver on .galwrap, no scroll-synced box.
     B. SCROLL LOCK is back, but only as html.dllb-lock{overflow:hidden} — never
        position:fixed on <body>, which reflows the page and jumps the scroll.
        The page behind is fully covered, so wheel / keys must not move it
        invisibly. Scroll position is untouched, so closing lands exactly where
        the customer was.
     C. THUMBNAIL STRIP, and the dialog is aria-modal with a light Tab loop
        (nothing behind it is visible any more). On open we still focus the
        CONTAINER, not the ✕ — Chromium paints :focus-visible on a programmatic
        focus from a cold page, and a ring on ✕ the instant it opens reads as an
        error state (review 2026-08-17). Esc / arrows are bound on document.
     D. NO OUTSIDE CLICKS exist any more (the overlay covers everything), so the
        old capture-phase outsideClick() swallow is gone.
     E. THE ✕ and the arrows are drawn by the backdrop itself (dl-lightbox.css):
        a glyph-shaped mask over backdrop-filter grayscale → contrast(100) →
        invert, so every pixel of the glyph is pure black or pure white, whichever
        contrasts with the pixel behind it — black on the white surround, white
        over a dark photo, and still readable over mid-grey, where a plain
        difference blend fades into the picture. Fallback (no backdrop-filter):
        the SVG glyph with mix-blend-mode:difference.
     F. CLOSE CONTRACT, unchanged in spirit: the photo zooms, thumbnails and
        arrows navigate, ✕ closes, ANY other click in the overlay closes (the
        white around the photo, the empty ends of the strip). A drag / pan /
        swipe release never does anything. Rules and order: onOverlayClick.

   ── WHY THE ODD BITS ARE THERE — do not "clean these up" ──────────────────────
   1. img.getAttribute("src"), never img.currentSrc / naturalWidth.
      The cells past the cap (.sh.over) are display:none above 820px and the imgs
      are loading="lazy", so the browser has never fetched them: currentSrc is ""
      and naturalWidth is 0. The attribute is always right, which is also why
      every photo is reachable from the viewer even when the grid hides some.
   2. sized(src, w): Shopify's CDN honours a width= param. The grid's srcset tops
      out at 1600w, useless enlarged, so the stage asks for 2400w and the
      thumbnails for 240w (2x of a 56px tile, plus headroom).
   3. No stopPropagation() on the gallery click. dl-product.js has a bubbling
      listener on .gal that moves the .sh.on highlight (variant <-> photo sync).
   4. Tap-vs-swipe slop (TAP_SLOP). Below 820px .gal is a scroll-snap carousel and
      a click can fire at the end of a swipe; travel >= 10px means "that was a
      swipe", so swiping the carousel never opens the viewer.
   5. .galwrap is never hidden or detached: dl-product.js observes it to slide the
      mobile .pbar in. We paint over it instead.
   6. Escape is bound on document in the CAPTURE phase and stops propagation ONLY
      while open: dl-search.js owns Escape and "/" in the bubbling phase.
   7. z-index 10090. Highest on the site: .dlk-toast 10080, .dlk.shm 10070,
      .dlk-dwr 10060, .dlk-ovl 10050 (dl-cart.css), .dls 10002 (dl-search.js),
      .dl-header-b-section 10000, .navx-drawer 9999, .pbar 40.
   8. Visibility is the `hidden` property — dl-tokens.css enforces
      [hidden]{display:none!important}, the house idiom. The fade-in is a class
      added one frame after unhiding.
   9. Wheel only zooms over the photo (or once zoomed).
  10. No dependency. Everything here is vanilla.
*/
(function () {
  "use strict";
  if (window.__dlLightbox) return;
  window.__dlLightbox = true;

  var D = document;
  var IMG_SEL = ".dl-scope .galwrap .gal .sh img";
  var BIG = 2400;        /* stage render width asked of the Shopify CDN */
  var THUMB = 240;       /* thumbnail render width */
  var TAP_SLOP = 10;     /* px of pointer travel that turns a tap into a swipe */
  var ZOOM = 2.5;        /* click / double-tap zoom factor */
  var ZOOM_MAX = 6;
  var TXT = {
    dialog: "Fotogalerie produktu",
    close: "Zavřít",
    prev: "Předchozí fotografie",
    next: "Další fotografie",
    thumb: "Fotografie "
  };

  var lb = null, els = null;
  var items = [], idx = 0;
  var isOpen = false;
  var opener = null;              /* the <img> that was clicked, for focus restore */

  /* zoom / pan state */
  var scale = 1, tx = 0, ty = 0;
  var pts = {}, start = null, pinch = null, dragged = false;
  var lastTap = 0, lastTapX = 0, lastTapY = 0, lastPointerType = "mouse";

  /* pointer travel of the CURRENT page gesture, for constraint 4 */
  var down = null, downLive = false;

  /* ---------- helpers ---------- */

  function sized(src, w) {
    if (!src) return "";
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, "$1width=" + w);
    return src + (src.indexOf("?") === -1 ? "?" : "&") + "width=" + w;
  }

  function dist(a, b) { var dx = a.x - b.x, dy = a.y - b.y; return Math.sqrt(dx * dx + dy * dy); }
  function ids() { return Object.keys(pts); }
  function two() { var k = ids(); return [pts[k[0]], pts[k[1]]]; }
  function attr(s) { return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;"); }

  /* Order is DOM order of .gal .sh — data-media is media.position and counts
     videos and 3D models, so it is NOT a contiguous index.
     2026-10-09 variant hero: a [hidden] cell is another colour's photo and is
     skipped, so a photo click shows the chosen colour, then the lifestyle set.
     all = true is the "Fotky všech variant" button: every .sh.vph, hidden or not. */
  function collect(gal, all) {
    var out = [];
    var cells = gal.querySelectorAll(all ? ".sh.vph" : ".sh");
    for (var i = 0; i < cells.length; i++) {
      if (!all && cells[i].hidden) continue;
      var img = cells[i].querySelector("img");
      if (!img) continue;
      var src = img.getAttribute("src");        /* constraint 1 */
      if (!src) continue;
      out.push({ src: src, alt: img.getAttribute("alt") || "", el: img });
    }
    return out;
  }

  /* ---------- overlay ---------- */

  var SVG_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';
  var SVG_L = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="square" aria-hidden="true"><path d="M15 3l-9 9 9 9"/></svg>';
  var SVG_R = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="square" aria-hidden="true"><path d="M9 3l9 9-9 9"/></svg>';

  function build() {
    lb = D.createElement("div");
    lb.className = "dllb";
    lb.hidden = true;
    lb.setAttribute("role", "dialog");
    lb.setAttribute("aria-modal", "true");
    lb.setAttribute("aria-label", TXT.dialog);
    lb.setAttribute("tabindex", "-1");          /* focus target on open — note C */
    lb.innerHTML =
      '<div class="dllb-stage" data-dllb-stage>' +
        '<img class="dllb-img" data-dllb-img alt="" draggable="false">' +
      '</div>' +
      '<span class="dllb-count" data-dllb-count aria-live="polite"></span>' +
      '<button type="button" class="dllb-x" data-dllb-x aria-label="' + TXT.close + '">' + SVG_X + '</button>' +
      '<button type="button" class="dllb-nav dllb-prev" data-dllb-prev aria-label="' + TXT.prev + '">' + SVG_L + '</button>' +
      '<button type="button" class="dllb-nav dllb-next" data-dllb-next aria-label="' + TXT.next + '">' + SVG_R + '</button>' +
      '<div class="dllb-strip" data-dllb-strip></div>';
    D.body.appendChild(lb);

    els = {
      stage: lb.querySelector("[data-dllb-stage]"),
      img: lb.querySelector("[data-dllb-img]"),
      count: lb.querySelector("[data-dllb-count]"),
      x: lb.querySelector("[data-dllb-x]"),
      prev: lb.querySelector("[data-dllb-prev]"),
      next: lb.querySelector("[data-dllb-next]"),
      strip: lb.querySelector("[data-dllb-strip]")
    };

    /* Never upscale past the natural size of what arrived: the natural size
       travels as two custom properties and the clamp lives in CSS, where the
       percentage resolves against the absolutely positioned box (see the 6:17
       photo note in dl-lightbox.css). The photo fades in once decoded. */
    els.img.addEventListener("load", function () {
      var n = els.img.naturalWidth, h = els.img.naturalHeight;
      els.img.style.setProperty("--lb-nw", n ? n + "px" : "100%");
      els.img.style.setProperty("--lb-nh", h ? h + "px" : "100%");
      els.img.classList.add("in");
      /* Chromium keeps painting the PREVIOUS photo's box until the new one has
         decoded, so a zoom started in that window was clamped against the wrong
         size and stayed off-centre once the new photo arrived. Re-clamp. */
      if (scale > 1) { clamp(); applyT(); }
    });

    /* A fresh press anywhere in the overlay clears the drag flag (capture phase,
       before bindStage()'s own pointerdown), so a stale flag from an earlier pan
       can never swallow the next genuine click on ✕ or a thumbnail. */
    lb.addEventListener("pointerdown", function () { dragged = false; }, true);
    lb.addEventListener("click", onOverlayClick);
    bindStage();
    return lb;
  }

  /* The thumbnails are rebuilt per opening: the gallery is per product page,
     but variant changes can re-render it, so never cache across openings. */
  function buildStrip() {
    var h = "";
    for (var i = 0; i < items.length; i++) {
      h += '<button type="button" class="dllb-th" data-dllb-i="' + i + '" aria-label="' + TXT.thumb + (i + 1) + '">' +
             '<img src="' + attr(sized(items[i].src, THUMB)) + '" alt="" loading="lazy" draggable="false">' +
           '</button>';
    }
    els.strip.innerHTML = h;
  }

  /* THE POINTER-CAPTURE RETARGETING TRAP (2026-08-17, still true).
     bindStage() calls setPointerCapture() on .dllb-stage so a pan / pinch keeps
     tracking outside it. Chromium then retargets the compatibility mouse events
     to the capture element, so a mouse click inside the stage arrives with
     e.target === .dllb-stage whatever was under the cursor. hitTarget() re-reads
     the real element under the pointer for genuine pointer clicks (detail > 0);
     keyboard / synthetic clicks keep e.target, which is right for them. */
  function hitTarget(e) {
    var t = e.target;
    if (!e.detail) return t;
    if (t !== els.stage && t !== lb) return t;
    var hit = D.elementFromPoint(e.clientX, e.clientY);
    return (hit && lb.contains(hit)) ? hit : t;
  }

  /* Is this client point over the photo AS PAINTED? Clamped to the stage, since
     a zoomed photo's box runs far outside it (overflow:hidden). */
  function onPhoto(x, y) {
    if (!els || !els.img || !els.img.getAttribute("src")) return false;
    if (typeof x !== "number" || typeof y !== "number") return false;
    var r = els.img.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    var s = els.stage.getBoundingClientRect();
    var l = Math.max(r.left, s.left), t = Math.max(r.top, s.top);
    var rt = Math.min(r.right, s.right), bt = Math.min(r.bottom, s.bottom);
    if (rt <= l || bt <= t) return false;
    return x >= l && x <= rt && y >= t && y <= bt;
  }

  /* CLOSE CONTRACT, evaluated strictly in this order:
       1. a drag / pan / swipe release -> nothing at all
       2. a thumbnail                  -> show that photo
       3. prev / next                  -> navigate
       4. ✕ (and any button added later) -> close
       5. the photo -> zoom (mouse); on touch double-tap in endPointer owns zoom
       6. ANYTHING ELSE in the overlay -> close: the white around the photo, the
          empty ends of the strip. A default, not an allow-list —
          nothing added later can become a dead zone.
     The drag guard runs first because the arrows sit over the stage: a swipe that
     ends on an arrow must not also fire that arrow. */
  function onOverlayClick(e) {
    var t = hitTarget(e);
    if (dragged) return;                                     /* 1 */
    var b = t.closest ? t.closest("button") : null;
    if (b) {
      if (b.hasAttribute("data-dllb-i")) return show(+b.getAttribute("data-dllb-i"));  /* 2 */
      if (b.hasAttribute("data-dllb-prev")) return go(-1);   /* 3 */
      if (b.hasAttribute("data-dllb-next")) return go(1);    /* 3 */
      return close();                                        /* 4 */
    }
    if (onPhoto(e.clientX, e.clientY) || t === els.img) {    /* 5 */
      if (lastPointerType !== "touch") toggleZoom(e.clientX, e.clientY);
      return;
    }
    close();                                                 /* 6 */
  }

  /* ---------- rendering ---------- */

  function preload(i) {
    var n = [i - 1, i + 1];
    for (var k = 0; k < n.length; k++) {
      var j = n[k];
      if (j < 0 || j >= items.length) continue;
      var im = new Image();
      im.src = sized(items[j].src, BIG);   /* neighbours only — never the whole set */
    }
  }

  function show(i) {
    if (!items.length) return;
    if (i < 0) i = items.length - 1;
    if (i > items.length - 1) i = 0;
    idx = i;
    resetZoom();
    var it = items[idx];
    var src = sized(it.src, BIG);
    if (els.img.getAttribute("src") !== src) {
      els.img.classList.remove("in");
      els.img.style.removeProperty("--lb-nw");
      els.img.style.removeProperty("--lb-nh");
      els.img.src = src;
      if (els.img.complete && els.img.naturalWidth) els.img.dispatchEvent(new Event("load"));
    }
    els.img.alt = it.alt;
    els.count.textContent = (idx + 1) + " / " + items.length;
    var ths = els.strip.children;
    for (var k = 0; k < ths.length; k++) {
      var on = k === idx;
      ths[k].classList.toggle("on", on);
      if (on) ths[k].setAttribute("aria-current", "true"); else ths[k].removeAttribute("aria-current");
    }
    centreThumb(ths[idx]);
    preload(idx);
  }

  /* Keep the active thumbnail in view without moving the page (the page is
     locked anyway, but scrollIntoView can still nudge ancestors on iOS). */
  function centreThumb(th) {
    if (!th) return;
    var s = els.strip;
    if (s.scrollWidth <= s.clientWidth) return;
    var want = th.offsetLeft - (s.clientWidth - th.offsetWidth) / 2;
    s.scrollTo({ left: Math.max(0, want), behavior: lb.classList.contains("on") ? "smooth" : "auto" });
  }

  function go(d) { show(idx + d); }

  /* ---------- zoom / pan ---------- */

  function applyT() {
    els.img.style.transform = scale === 1 ? "" : "translate(" + tx + "px," + ty + "px) scale(" + scale + ")";
    els.img.classList.toggle("zoomed", scale > 1);
  }
  function resetZoom() { scale = 1; tx = 0; ty = 0; pinch = null; if (els) applyT(); }
  function clamp() {
    var r = els.stage.getBoundingClientRect();
    var mx = Math.max(0, (els.img.offsetWidth * scale - r.width) / 2);
    var my = Math.max(0, (els.img.offsetHeight * scale - r.height) / 2);
    tx = Math.min(mx, Math.max(-mx, tx));
    ty = Math.min(my, Math.max(-my, ty));
  }
  /* Zoom to s keeping the client point (px,py) pinned. CSS applies
     translate(t) scale(s) as p = centre + d*s + t, so d = (p - centre)/s0. */
  function zoomTo(s, px, py) {
    s = Math.max(1, Math.min(ZOOM_MAX, s));
    var r = els.img.getBoundingClientRect();
    var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    var dx = scale ? (px - cx) / scale : 0, dy = scale ? (py - cy) / scale : 0;
    var bx = cx - tx, by = cy - ty;
    scale = s;
    tx = px - dx * s - bx;
    ty = py - dy * s - by;
    if (scale === 1) { tx = 0; ty = 0; }
    clamp();
    applyT();
  }
  function toggleZoom(px, py) {
    if (scale > 1) { resetZoom(); return; }
    var r = els.stage.getBoundingClientRect();
    zoomTo(ZOOM, px == null ? r.left + r.width / 2 : px, py == null ? r.top + r.height / 2 : py);
  }

  function bindStage() {
    var st = els.stage;
    st.addEventListener("pointerdown", function (e) {
      lastPointerType = e.pointerType || "mouse";
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      var n = ids().length;
      if (n === 1) {
        start = { x: e.clientX, y: e.clientY, tx: tx, ty: ty, t: Date.now() };
        dragged = false;
        els.img.classList.add("live");
        if (st.setPointerCapture) { try { st.setPointerCapture(e.pointerId); } catch (err) {} }
      } else if (n === 2) {
        var p = two();
        pinch = { d: dist(p[0], p[1]), s: scale, mx: (p[0].x + p[1].x) / 2, my: (p[0].y + p[1].y) / 2 };
        start = null;
        dragged = true;
      }
    });
    st.addEventListener("pointermove", function (e) {
      if (!pts[e.pointerId]) return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (ids().length >= 2 && pinch) {
        var p = two();
        if (pinch.d > 0) zoomTo(pinch.s * (dist(p[0], p[1]) / pinch.d), pinch.mx, pinch.my);
        e.preventDefault();
        return;
      }
      if (!start) return;
      var dx = e.clientX - start.x, dy = e.clientY - start.y;
      if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) dragged = true;
      if (scale > 1) { tx = start.tx + dx; ty = start.ty + dy; clamp(); applyT(); e.preventDefault(); }
    }, { passive: false });
    st.addEventListener("pointerup", endPointer);
    st.addEventListener("pointercancel", endPointer);
    st.addEventListener("wheel", function (e) {
      if (scale === 1 && e.target !== els.img) return;
      e.preventDefault();
      var f = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0015));
      zoomTo(scale * f, e.clientX, e.clientY);
    }, { passive: false });
  }

  function endPointer(e) {
    var had = pts[e.pointerId];
    delete pts[e.pointerId];
    if (ids().length < 2) pinch = null;
    if (!had) return;
    els.img.classList.remove("live");
    if (start && ids().length === 0) {
      var dx = e.clientX - start.x, dy = e.clientY - start.y, dt = Date.now() - start.t;
      /* Swipe-to-navigate only while un-zoomed, otherwise panning is impossible. */
      if (scale === 1 && Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.4 && dt < 900) {
        go(dx < 0 ? 1 : -1);
      } else if (!dragged && (e.pointerType === "touch" || e.pointerType === "pen")) {
        var now = Date.now();
        if (now - lastTap < 320 && Math.abs(e.clientX - lastTapX) < 28 && Math.abs(e.clientY - lastTapY) < 28) {
          toggleZoom(e.clientX, e.clientY);
          lastTap = 0;
        } else { lastTap = now; lastTapX = e.clientX; lastTapY = e.clientY; }
      }
      start = null;
    }
    if (scale === 1 && (tx || ty)) { tx = 0; ty = 0; applyT(); }
  }

  /* ---------- open / close ---------- */

  function openFrom(img) {
    var gal = img.closest(".gal");
    if (!gal) return;
    var list = collect(gal);
    var at = 0;
    for (var i = 0; i < list.length; i++) if (list[i].el === img) { at = i; break; }
    openList(list, at, img);
  }

  function openList(list, at, from, label) {
    if (!list.length) return;
    items = list;
    opener = from;
    if (!lb) build();
    lb.setAttribute("aria-label", label || TXT.dialog);
    var one = items.length < 2;
    lb.classList.toggle("one", one);
    els.prev.hidden = one;
    els.next.hidden = one;
    els.strip.hidden = one;
    buildStrip();
    D.documentElement.classList.add("dllb-lock");   /* note B */
    lb.hidden = false;
    isOpen = true;
    show(at);
    /* fade in one frame after unhiding, so the transition has a start state */
    window.requestAnimationFrame(function () { if (isOpen) lb.classList.add("on"); });
    if (lb.focus) { try { lb.focus({ preventScroll: true }); } catch (err) { lb.focus(); } }
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    resetZoom();
    lb.classList.remove("on");
    lb.hidden = true;
    D.documentElement.classList.remove("dllb-lock");
    els.img.removeAttribute("src");
    els.img.classList.remove("in");
    els.strip.innerHTML = "";
    pts = {}; start = null; dragged = false;
    if (opener) {
      /* an <img> needs tabindex to take focus; a button (data-dllb-set) already has it */
      if (opener.tagName === "IMG" && !opener.hasAttribute("tabindex")) opener.setAttribute("tabindex", "-1");
      if (opener.focus) { try { opener.focus({ preventScroll: true }); } catch (err) { opener.focus(); } }
      opener = null;
    }
  }

  /* ---------- page bindings ---------- */

  /* Constraint 4: measure the travel of the whole gesture, not just down->click,
     because a scroll-snap swipe can end back where it started. */
  D.addEventListener("pointerdown", function (e) {
    down = { x: e.clientX, y: e.clientY, moved: 0 };
    downLive = true;
  }, true);
  D.addEventListener("pointermove", function (e) {
    if (!downLive || !down) return;
    var d = Math.abs(e.clientX - down.x) + Math.abs(e.clientY - down.y);
    if (d > down.moved) down.moved = d;
  }, { capture: true, passive: true });
  D.addEventListener("pointerup", function () { downLive = false; }, true);
  D.addEventListener("pointercancel", function () { downLive = false; down = null; }, true);

  /* Constraint 3: plain bubbling listener, no stopPropagation, no preventDefault. */
  D.addEventListener("click", function (e) {
    if (isOpen) return;
    if (!e.target || !e.target.closest) return;
    var set = e.target.closest(".dl-scope .galwrap [data-dllb-set]");
    if (set) {
      var g = set.closest(".galwrap").querySelector(".gal");
      if (g) openList(collect(g, true), 0, set);
      return;
    }
    var img = e.target.closest(IMG_SEL);
    if (!img) return;
    if (e.detail > 0 && down && down.moved >= TAP_SLOP) return;   /* that was a swipe */
    openFrom(img);
  });

  /* Constraint 6 + note C: keys only while open. Tab loops inside the viewer
     (✕, arrows, thumbnails), since nothing behind it is visible. */
  D.addEventListener("keydown", function (e) {
    if (!isOpen) return;
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (e.key === "ArrowLeft") { e.preventDefault(); e.stopPropagation(); go(-1); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); e.stopPropagation(); go(1); return; }
    if (e.key === "Tab") {
      var f = Array.prototype.filter.call(lb.querySelectorAll("button"), function (b) { return !b.hidden && b.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1], a = D.activeElement;
      if (e.shiftKey && (a === first || a === lb)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (a === last || !lb.contains(a))) { e.preventDefault(); first.focus(); }
    }
  }, true);

  window.DLLightbox = {
    open: function (i) {
      var img = D.querySelector(IMG_SEL);
      if (!img) return;
      openFrom(img);
      if (typeof i === "number") show(i);
    },
    close: close,
    /* 2026-10-12 (realizace-portfolio): open the viewer on ANY list of photos, not
       just the PDP gallery. list = [{ src, alt }], src a Shopify CDN URL (sized()
       re-asks it at 2400w / 240w); at = start index; from = the element that gets
       focus back on close; label = the dialog's accessible name. Additive: the PDP
       path above is untouched. Used by assets/dl-realizace.js. */
    openList: function (list, at, from, label) {
      var clean = [];
      for (var i = 0; i < (list || []).length; i++) {
        if (list[i] && list[i].src) clean.push({ src: list[i].src, alt: list[i].alt || "", el: null });
      }
      if (!clean.length) return;
      var n = Math.max(0, Math.min(clean.length - 1, at | 0));
      openList(clean, n, from || null, label);
    }
  };
})();
